import React, { useState, useEffect, useCallback } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import notificationService from 'services/notificationService';
import { dineOpsService } from 'api/services/dineOpsService';
import type { DinePriceList } from 'api/types';

/**
 * The price list header: code, name, which channels and classes it applies
 * to, its window, priority. On a version in force only name, priority and
 * active can change (the API refuses the rest); the dialog says so and locks
 * those fields.
 */
interface Props {
  isOpen: boolean;
  mode: 'create' | 'edit';
  record?: DinePriceList | null;
  /** The saved list; a new one is opened straight on its Prices step. */
  onSaved: (saved: DinePriceList) => void;
  onClose: () => void;
}

const CHANNELS = [['DINE_IN', 'Dine-in'], ['ROOM_SERVICE', 'Room service'], ['TAKEAWAY', 'Takeaway']] as const;
const CLASSES = [['IN_HOUSE', 'In-house guest'], ['OUTSIDE', 'Outside guest'], ['STAFF', 'Staff'], ['COMPLIMENTARY', 'Complimentary']] as const;
const codeFrom = (name: string) => name.trim().toUpperCase().replace(/[^A-Z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 30);
const today = () => new Date().toISOString().slice(0, 10);

const DineOpsPriceListModal: React.FC<Props> = ({ isOpen, mode, record, onSaved, onClose }) => {
  const [code, setCode] = useState('');
  const [codeTouched, setCodeTouched] = useState(false);
  const [name, setName] = useState('');
  const [channels, setChannels] = useState<string[]>([]);
  const [classes, setClasses] = useState<string[]>([]);
  const [priority, setPriority] = useState('10');
  const [from, setFrom] = useState(today());
  const [to, setTo] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const frozen = mode === 'edit' && record?.state !== 'UPCOMING';

  useEffect(() => {
    if (!isOpen) return;
    setError(null); setCodeTouched(false);
    if (mode === 'edit' && record) {
      setCode(record.code); setName(record.name); setChannels(record.orderChannels); setClasses(record.customerClasses);
      setPriority(String(record.priority)); setFrom(record.validFrom.slice(0, 10)); setTo(record.validTo?.slice(0, 10) ?? ''); setIsActive(record.isActive);
    } else {
      setCode(''); setName(''); setChannels([]); setClasses([]); setPriority('10'); setFrom(today()); setTo(''); setIsActive(true);
    }
  }, [isOpen, mode, record]);

  const toggle = (list: string[], v: string, set: (x: string[]) => void) => set(list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);

  const save = useCallback(async () => {
    if (!name.trim()) { setError('A price list needs a name.'); return; }
    if (mode === 'create' && !/^[A-Z][A-Z0-9_]{1,29}$/.test(code.trim())) { setError('The code is upper-case letters, digits and underscores, e.g. ROOM_SERVICE.'); return; }
    if (!from) { setError('A start date is needed.'); return; }
    if (to && to < from) { setError('The last day cannot be before the first.'); return; }
    setSaving(true); setError(null);
    try {
      if (mode === 'edit' && record) {
        const saved = await dineOpsService.updatePriceList(record.id, {
          name: name.trim(), orderChannels: channels, customerClasses: classes, priority: Number(priority) || 0,
          validFrom: from, validTo: to || undefined, isActive, rowVersion: record.rowVersion,
        });
        notificationService.success('Saved', `'${code}' updated.`);
        onSaved(saved);
      } else {
        const saved = await dineOpsService.createPriceList({
          code: code.trim(), name: name.trim(), orderChannels: channels, customerClasses: classes, priority: Number(priority) || 0,
          validFrom: from, validTo: to || undefined, prices: [],
        });
        notificationService.success('Created', `'${code.trim()}' created. Step 2: type the prices.`);
        onSaved(saved);
      }
    } catch (err: any) {
      setError(err?.response?.data?.message ?? 'The price list could not be saved.');
    } finally {
      setSaving(false);
    }
  }, [mode, record, code, name, channels, classes, priority, from, to, isActive, onSaved]);

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-lg" style={{ minHeight: 'auto' }}>
        <div className="modal-header">
          <h2 className="modal-title">{mode === 'create' ? 'New price list' : `Edit — ${record?.code}`}</h2>
          <button className="modal-close" onClick={onClose} disabled={saving}><IconSystem.x size={18} /></button>
        </div>
        <div className="modal-content" style={{ maxHeight: '65vh', overflowY: 'auto' }}>
          {error && <div className="form-error-message" style={{ marginBottom: 12 }}>{error}</div>}
          {frozen && (
            <div style={{ fontSize: 12, opacity: 0.8, marginBottom: 10, padding: '6px 10px', border: '1px solid var(--border-color, #e5e7eb)', borderRadius: 6 }}>
              <IconSystem.lock size={12} /> This version is {record?.state === 'CURRENT' ? 'in force' : 'finished'}. Its dates, channels and classes are history — bills point at them.
              Only the name, priority and active flag can change here. For new dates or scope use <strong>New version from a date</strong>.
            </div>
          )}

          {mode === 'create' && (
            <div style={{ fontSize: 12, opacity: 0.8, marginBottom: 10, padding: '6px 10px', border: '1px solid var(--border-color, #e5e7eb)', borderRadius: 6 }}>
              <strong>Step 1 of 2 — who and when.</strong> This says which orders the list applies to and for which dates.
              <strong> Step 2 — the prices</strong> opens right after Save: there you type the amount each dish costs under this list
              (e.g. Butter Chicken 430 instead of 380). A dish you leave blank keeps its card price.
            </div>
          )}

          <div className="form-grid-2">
            <div className="form-group">
              <label className="form-label">Name <span className="text-danger">*</span></label>
              <input type="text" className="form-input" value={name} autoFocus maxLength={100}
                onChange={(e) => { setName(e.target.value); if (mode === 'create' && !codeTouched) setCode(codeFrom(e.target.value)); }}
                placeholder="e.g., Room service, Weekend lunch offer" />
            </div>
            <div className="form-group">
              <label className="form-label">Code {mode === 'create' && <span className="text-danger">*</span>}</label>
              <input type="text" className="form-input" value={code} disabled={mode === 'edit'} maxLength={30}
                onChange={(e) => { setCodeTouched(true); setCode(e.target.value.toUpperCase()); }} placeholder="ROOM_SERVICE" />
              <div style={{ fontSize: 12, opacity: 0.7, marginTop: 4 }}>Every version of this list shares the code.</div>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Applies to order channels <span style={{ opacity: 0.6 }}>(none ticked = every channel)</span></label>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              {CHANNELS.map(([v, l]) => (
                <label key={v} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <input type="checkbox" checked={channels.includes(v)} disabled={frozen} onChange={() => toggle(channels, v, setChannels)} /> {l}
                </label>
              ))}
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Applies to customer classes <span style={{ opacity: 0.6 }}>(none ticked = every class)</span></label>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              {CLASSES.map(([v, l]) => (
                <label key={v} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <input type="checkbox" checked={classes.includes(v)} disabled={frozen} onChange={() => toggle(classes, v, setClasses)} /> {l}
                </label>
              ))}
            </div>
          </div>

          <div className="form-grid-2">
            <div className="form-group">
              <label className="form-label">Valid from <span className="text-danger">*</span></label>
              <input type="date" className="form-input" value={from} disabled={frozen} onChange={(e) => setFrom(e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Valid to <span style={{ opacity: 0.6 }}>(blank = open-ended)</span></label>
              <input type="date" className="form-input" value={to} min={from} disabled={frozen} onChange={(e) => setTo(e.target.value)} />
            </div>
          </div>

          <div className="form-grid-2">
            <div className="form-group">
              <label className="form-label">Priority</label>
              <input type="number" className="form-input" value={priority} min={0} onChange={(e) => setPriority(e.target.value)} />
              <div style={{ fontSize: 12, opacity: 0.7, marginTop: 4 }}>When two lists match a line, the higher priority wins. The card price is below every list.</div>
            </div>
            {mode === 'edit' && (
              <div className="form-group" style={{ alignSelf: 'end' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} /> <span>Active (switched off, it prices nothing)</span>
                </label>
              </div>
            )}
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={() => void save()} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </>
  );
};

export default DineOpsPriceListModal;
